/**
 * Thermometer units (owner, 2026-10-09: "Thermometers need a C toggle"). One canonical unit for the rules, °F, as the
 * Food Code, the state packs and every template are written; the unit and the exact number the person entered kept beside
 * it, so nothing is lost or rounded twice.
 *
 * Gates:
 * | T1 One conversion | `convertTemp` is the only place C = (F − 32) × 5/9 is written (units.ts and easyfill.ts call it) |
 * | T2 Canonical °F, exact | a °C entry is stored as its exact °F (6°C → 42.8°F), float noise trimmed at 6 decimals, so a
 *   reading converts back to the same number it was typed as (no drift) |
 * | T3 Rules compare °F | every limit stays the pack's °F number; a °C reading is judged by its exact °F (6°C = 42.8°F fails
 *   41°F; 5°C = 41°F passes) |
 * | T4 Entered kept | the record keeps `{ value, unit }` as typed (cellSources[column].entered), shown as "entered as 6°C" |
 * | T5 Display | readings: whole °F when whole (a fraction is never rounded away: 41.2°F stays 41.2°F, so a failing reading
 *   never reads as the limit), one decimal °C (5.0°C). Limits and lines: °C to one decimal, ".0" dropped ("Keep at 5°C or
 *   colder", "57.2°C"), since a rounded 57°C would be a reading that fails 135°F |
 * | T6 Inspector | exports show both: "41°F (5.0°C)", plus "entered as 6°C" when it was typed in °C |
 * | T7 Whose unit | a person's own choice, else this device's (a tap before it synced), else the business default, else °F. A
 *   shared device (kiosk) remembers its own choice; nobody's profile is written from it |
 */
import type { UnitPref } from "./units.ts";

export type TempUnit = "F" | "C";
export const TEMP_UNITS: readonly TempUnit[] = ["F", "C"];
/** What the person typed, as they typed it, and in which unit (T4). */
export interface EnteredTemp { value: string; unit: TempUnit }

export const isTempUnit = (v: unknown): v is TempUnit => v === "F" || v === "C";
const trim6 = (n: number) => Math.round(n * 1e6) / 1e6;

/** The one conversion (T1): C = (F − 32) × 5/9 and back. Exact up to float noise. */
export function convertTemp(n: number, from: TempUnit, to: TempUnit): number {
  if (from === to) return n;
  return trim6(from === "C" ? (n * 9) / 5 + 32 : ((n - 32) * 5) / 9);
}
/** A reading in the canonical unit (T2). */
export const toCanonicalF = (n: number, unit: TempUnit) => convertTemp(n, unit, "F");

const NUM = /(-?\d+(?:[.,]\d+)?)/;
/** "6°C", "6 c", "6 degrees celsius", "41F", "41", "about 41" → the number and the unit it was written in (none: unknown). */
export function parseTemp(raw: string | null | undefined): { n: number; value: string; unit?: TempUnit } | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  const m = s.match(new RegExp(`${NUM.source}\\s*(?:°|º|˚|deg(?:rees?)?\\.?)?\\s*(celsius|centigrade|fahrenheit|c|f)?(?![a-z])`, "i"));
  if (!m) return undefined;
  const value = m[1]!.replace(",", ".");
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  const u = m[2]?.toLowerCase();
  const unit: TempUnit | undefined = !u ? undefined : u.startsWith("c") ? "C" : "F";
  return { n, value: String(n), ...(unit ? { unit } : {}) };
}

/** A typed number tagged with its unit, the way forms send it ("6°C"). The server reads it back with `parseTemp`. */
export function tagTemp(typed: string, unit: TempUnit): string {
  const t = typed.trim();
  return t === "" ? "" : `${t}°${unit}`;
}

/** The number a person reads (T5): °C to one decimal; °F whole when whole, else one decimal. */
export function tempNumber(n: number, unit: TempUnit): string {
  if (unit === "C") return (Math.round(n * 10) / 10).toFixed(1).replace(/^-0\.0$/, "0.0");
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}
/**
 * A limit or a number inside a sentence: one decimal at most, ".0" dropped ("5°C", "41°F"). `side`: which way to round so
 * the shown limit itself passes ("135°F or above" is 57.3°C, since 57.2°C is 134.96°F and fails; "70°F or less" is 21.1°C).
 */
export function limitNumber(f: number, unit: TempUnit, side?: "up" | "down"): string {
  const x = convertTemp(f, "F", unit) * 10;
  const r = (side === "up" ? Math.ceil(x - 1e-6) : side === "down" ? Math.floor(x + 1e-6) : Math.round(x)) / 10;
  return String(Object.is(r, -0) ? 0 : r);
}
export const limitShown = (f: number, unit: TempUnit, side?: "up" | "down") => `${limitNumber(f, unit, side)}°${unit}`;

/** Which way a limit in a sentence points, from the words around it. */
function sideOf(before: string, after: string): "up" | "down" | undefined {
  if (/^\s*(?:or|and)\s+(?:below|less|colder|lower|under)\b/i.test(after) || /\b(?:under|below|at most|no (?:more|warmer) than|to)\s*$/i.test(before)) return "down";
  if (/^\s*(?:or|and)\s+(?:above|more|hotter|warmer|higher|over)\b/i.test(after) || /\b(?:at least|above|over|hotter than)\s*$/i.test(before)) return "up";
  return undefined;
}

/** A stored reading (canonical °F) in the viewer's unit (T5). The exact entered value is used when it is in that unit. */
export function readingShown(storedF: string | number | null | undefined, unit: TempUnit, entered?: EnteredTemp): string {
  if (storedF == null || storedF === "") return "";
  if (entered && entered.unit === unit && Number.isFinite(Number(entered.value))) return `${tempNumber(Number(entered.value), unit)}°${unit}`;
  const f = Number(storedF);
  if (!Number.isFinite(f)) return String(storedF);
  return `${tempNumber(convertTemp(f, "F", unit), unit)}°${unit}`;
}
/** Both units, °F first (T6): "41°F (5.0°C)". */
export function readingBoth(storedF: string | number | null | undefined, entered?: EnteredTemp): string {
  const f = readingShown(storedF, "F", entered);
  return f ? `${f} (${readingShown(storedF, "C", entered)})` : "";
}
/** "entered as 6°C" (T4): the number as it was typed. */
export const enteredLine = (e: EnteredTemp) => `entered as ${e.value}°${e.unit}`;

/** A number the person typed in their unit → the canonical °F string a log stores (T2). */
export function storedFromTyped(typed: number, unit: TempUnit): string {
  return String(toCanonicalF(typed, unit));
}
/** A stored °F string → the number shown in an input in the person's unit (exact, one decimal for °C). */
export function typedFromStored(storedF: string | null | undefined, unit: TempUnit): string {
  if (storedF == null || storedF === "") return "";
  const n = Number(storedF);
  if (!Number.isFinite(n)) return "";
  if (unit === "F") return String(n);
  return String(Math.round(convertTemp(n, "F", "C") * 100) / 100);
}

/**
 * Every "41°F" in a line of text, in the reader's unit (T5): limits, rule messages, server lines. "±2°F" is a difference
 * (×5/9, no offset). Ranges "165–194°F" convert both ends. °F lines come back unchanged.
 */
export function showTemps(text: string, unit: TempUnit, side?: "up" | "down"): string {
  if (unit === "F" || !text) return text;
  return text.replace(/(±\s*)?(-?\d+(?:\.\d+)?)(?:\s*(?:–|-|to)\s*(-?\d+(?:\.\d+)?))?\s*°\s*F\b/g, (m: string, pm: string | undefined, a: string, b: string | undefined, at: number) => {
    if (pm) return `${pm}${String(Math.round(((Number(a) * 5) / 9) * 10) / 10)}°C`;
    // A range keeps both ends inside it: the low end up, the high end down.
    if (b) return `${limitNumber(Number(a), "C", "up")}–${limitNumber(Number(b), "C", "down")}°C`;
    return limitShown(Number(a), "C", side ?? sideOf(text.slice(Math.max(0, at - 24), at), text.slice(at + m.length, at + m.length + 24)));
  });
}

/**
 * Readings inside a line (a record's "42.8°F", "Walk-in 38°F"), in the reader's unit as readings are shown (T5): one decimal
 * °C, "6.0°C". Limits and rule lines use `showTemps`/`ruleLine` instead. °F lines come back unchanged.
 */
export function showReadings(text: string, unit: TempUnit): string {
  if (unit === "F" || !text) return text;
  return text.replace(/(-?\d+(?:\.\d+)?)\s*°\s*F\b/g, (_m: string, a: string) => `${tempNumber(convertTemp(Number(a), "F", "C"), "C")}°C`);
}

/** A rule's message in the reader's unit, rounded the rule's way ("<=": down, ">=": up), so the shown limit passes. */
export function ruleLine(message: string, op: string | undefined, unit: TempUnit): string {
  return showTemps(message, unit, op === "<=" || op === "<" ? "down" : op === ">=" || op === ">" ? "up" : undefined);
}

/** Whose unit applies (T7). `shared`: a kiosk or shared tablet, where the device's own choice wins over anyone's profile. */
export function tempUnitFor(o: { person?: TempUnit | null; device?: TempUnit | null; business?: TempUnit | null; shared?: boolean }): TempUnit {
  if (o.shared) return o.device ?? o.business ?? "F";
  return o.person ?? o.device ?? o.business ?? "F";
}
/** The display preference the step screens already use ("°F · °C" on a step): °C = metric. */
export const unitPrefOf = (u: TempUnit): UnitPref => (u === "C" ? "metric" : "us");
export const tempUnitOf = (p: UnitPref | null | undefined): TempUnit => (p === "metric" ? "C" : "F");

/** The one-line hint the first time someone sees the toggle (passability: one sentence, the action first). */
export const TEMP_TOGGLE_HINT = "Tap °F or °C to match your thermometer. We remember it.";
/** When a photo or voice reading had no unit we could read: ask, never guess (owner brief 4). */
export const TEMP_UNIT_ASK = "Is that °F or °C?";
